import { Column, Header, Table } from '@tanstack/react-table';
import { cn } from '@sib/ui';
import {
  getDataTableHeaderCellCssVars,
  getTableColumnPinningClassName,
  getTableHeaderWidthClassName,
} from '../../utils';
import { getTableColumnPinningProperties } from '../../utils/utils';

export enum DataTableHeaderRow {
  Group = 'group',
  Leaf = 'leaf',
}

interface GetDataTableHeaderStickyClassNameProps {
  row: DataTableHeaderRow;
  withColumnGroups?: boolean;
  isPinned?: boolean;
}

export const getDataTableHeaderStickyClassName = ({
  row,
  withColumnGroups = false,
  isPinned = false,
}: GetDataTableHeaderStickyClassNameProps) => {
  const topClass =
    row === DataTableHeaderRow.Group || !withColumnGroups
      ? 'sticky top-0'
      : 'sticky top-[var(--data-table-group-header-height,2.25rem)]';

  return cn(topClass, 'bg-primary', isPinned ? 'z-30' : 'z-20');
};

interface GetDataTableHeaderCellClassNameProps<T> {
  header: Header<T, unknown>;
  row: DataTableHeaderRow;
  withFixedColumnWidth: boolean;
  withColumnGroups?: boolean;
}

export const getDataTableHeaderCellClassName = <T>({
  header,
  row,
  withFixedColumnWidth,
  withColumnGroups = false,
}: GetDataTableHeaderCellClassNameProps<T>) => {
  const { pinPosition } = getTableColumnPinningProperties(header.column);
  const isPinned = Boolean(pinPosition);

  return cn(
    getDataTableHeaderStickyClassName({ row, withColumnGroups, isPinned }),
    getTableHeaderWidthClassName(withFixedColumnWidth),
    getTableColumnPinningClassName(pinPosition)
  );
};

export const getDataTableHeaderCellStyle = <T>(header: Header<T, unknown>) => {
  const { pinPosition } = getTableColumnPinningProperties(header.column);

  return getDataTableHeaderCellCssVars(
    header.id,
    header.column.id,
    pinPosition
  );
};

const getTopLevelGroupColumns = <T>(table: Table<T>) =>
  table
    .getAllColumns()
    .filter(column => column.depth === 0 && column.columns.length);

const getGroupColumn = <T>(column: Column<T, unknown>) => {
  if (column.columns.length) return column;

  const parent = column.parent;

  if (!parent || parent.depth < 0) return null;

  return parent;
};

interface GetColumnGroupBorderClassNameProps {
  hasStartBorder?: boolean;
  hasEndBorder?: boolean;
}

export const getColumnGroupBorderClassName = ({
  hasStartBorder = false,
  hasEndBorder = false,
}: GetColumnGroupBorderClassNameProps) =>
  cn(
    hasStartBorder &&
      'before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-white before:content-[""]',
    hasEndBorder &&
      'after:absolute after:inset-y-0 after:right-0 after:w-0.5 after:bg-white after:content-[""]'
  );

export const getGroupHeaderColumnBorderSides = <T>(
  column: Column<T, unknown>,
  table: Table<T>
) => {
  if (!column.columns.length)
    return { hasStartBorder: false, hasEndBorder: false };

  const topLevelGroupColumns = getTopLevelGroupColumns(table);
  const isFirstGroup = topLevelGroupColumns[0]?.id === column.id;

  return {
    hasStartBorder: isFirstGroup,
    hasEndBorder: true,
  };
};

export const getLeafColumnGroupBorderSides = <T>(
  column: Column<T, unknown>,
  table: Table<T>
) => {
  const groupColumn = getGroupColumn(column);

  if (!groupColumn?.columns.length)
    return { hasStartBorder: false, hasEndBorder: false };

  const siblings = groupColumn.columns;
  const columnIndex = siblings.findIndex(sibling => sibling.id === column.id);
  const isFirstInGroup = columnIndex === 0;
  const isLastInGroup = columnIndex === siblings.length - 1;
  const isFirstGroup = getTopLevelGroupColumns(table)[0]?.id === groupColumn.id;

  return {
    hasStartBorder: isFirstInGroup && isFirstGroup,
    hasEndBorder: isLastInGroup,
  };
};
